import ButtonBase from "@mui/material/ButtonBase";
import Switch from "@mui/material/Switch";
import { useHosts } from "../api/queries";
import { PMultiSelect } from "../ui/PMultiSelect";
import {
  PLAYER_OPTIONS,
  selectedPlayerOptions,
  setPlayerEnabled,
  setPlayerHosts,
  setPlayerOptions,
  type FilterPlayer as Player,
  type PlayerOption,
} from "./filter-logic";

// PORT OF `filter-player`: A `card w-full` WITH THE BOLD `text-lg` TITLE AND AN ENABLE SWITCH;
// (`mt-5`) A MULTIPLE `p-selectButton` OF CARDED / UNHOSTED / UNCARDED; (`mt-5`) A "SELECT
// HOSTS" `p-multiSelect` OF EVERY HOST BUT `userId` 0; AND, WHEN ASKED FOR, AN "UNHOSTED"
// SWITCH ROW. EVERYTHING BELOW THE TITLE IS DISABLED WHILE THE SECTION IS OFF.
export function FilterPlayer(props: { title: string; data: Player; showOptions: boolean; showUnhosted: boolean; onChange: (p: Player) => void }) {
  const { title, data, showOptions, showUnhosted, onChange } = props;
  const hosts = useHosts();
  const hostOptions = (hosts.data ?? []).filter((h) => h.userId !== 0).map((h) => ({ label: h.hostName, value: h.id }));
  const selected = selectedPlayerOptions(data);
  const off = !data.enabled;

  const toggle = (v: PlayerOption) => onChange(setPlayerOptions(data, selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v]));

  return (
    <div className="card w-full">
      <div className="flex flex-row align-items-center justify-content-between">
        <div className="font-bold text-lg">{title}</div>
        <div>
          <Switch checked={data.enabled} onChange={(_, on) => onChange(setPlayerEnabled(data, on))} slotProps={{ input: { "aria-label": title } }} />
        </div>
      </div>
      {showOptions && (
        <div className="mt-5">
          {/* `p-selectButton [multiple]="true"` IN `p-fluid`: JOINED EQUAL SEGMENTS, `--primary-700` WHEN ON */}
          <div className="p-inputgroup flex w-full" role="group" aria-label="Player options" style={off ? { opacity: 0.65 } : undefined}>
            {PLAYER_OPTIONS.map((o) => {
              const on = selected.includes(o.value);
              return (
                <ButtonBase
                  key={o.value}
                  aria-pressed={on}
                  disabled={off}
                  disableRipple
                  onClick={() => toggle(o.value)}
                  sx={{
                    flex: "1 1 0",
                    padding: "0.5rem 0.75rem",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    lineHeight: "normal",
                    color: "#ffffff",
                    backgroundColor: on ? "var(--primary-700)" : "var(--primary-500)",
                    transition: "background-color 0.15s",
                    "&:hover": { backgroundColor: on ? "var(--primary-700)" : "var(--primary-600)" },
                  }}
                >
                  {o.label}
                </ButtonBase>
              );
            })}
          </div>
        </div>
      )}
      <div className="mt-5">
        <PMultiSelect
          value={data.hostIds ?? []}
          options={hostOptions}
          disabled={off}
          placeholder="Select Hosts"
          onChange={(ids) => onChange(setPlayerHosts(data, ids))}
        />
      </div>
      {showUnhosted && (
        <div className="mt-5 flex flex-row align-items-center justify-content-between">
          <div className="font-bold">Unhosted</div>
          <Switch
            checked={data.unhosted}
            disabled={off}
            onChange={(_, unhosted) => onChange({ ...data, unhosted, hostIds: unhosted ? [] : data.hostIds })}
            slotProps={{ input: { "aria-label": "Unhosted" } }}
          />
        </div>
      )}
    </div>
  );
}
